import { demoProject } from "./defaultProject";
import { COLS, emptyRow, type Cell, type Network, type Project } from "./types";

const uid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 9)}`;

const c = (type: Cell["type"], operand = "", extra: Partial<Cell> = {}): Cell => ({
  type,
  operand,
  ...extra,
});

function net(title: string, build: (rows: Cell[][], links: boolean[][]) => void, rowCount = 2): Network {
  const rows = Array.from({ length: rowCount }, () => emptyRow());
  const links = Array.from({ length: rowCount }, () =>
    Array.from({ length: COLS + 1 }, () => false),
  );
  build(rows, links);
  return { id: uid("net"), title, comment: "", rows, links };
}

const w = (from: string, to: string, color: string) => ({ id: uid("w"), from, to, color });

function base(name: string, tags: Record<string, string>, networks: Network[]): Project {
  const p = demoProject();
  p.id = uid("prj");
  p.name = name;
  p.tags = tags;
  p.snap = true;
  p.networks = networks;
  return p;
}

/* ------------------------------------------------------------- examples */

function startStop(): Project {
  return base(
    "Start / Stop",
    { "I0.0": "START", "I0.1": "STOP", "Q0.0": "KM1", "Q0.1": "LAMP_RUN" },
    [
      net("Network 1 — Start / Stop", (rows, links) => {
        rows[0]![0] = c("NO", "I0.0");
        rows[0]![1] = c("NO", "I0.1");
        rows[0]![4] = c("COIL", "Q0.0");
        rows[1]![0] = c("NO", "Q0.0");
        links[0]![1] = true;
      }),
      net("Network 2 — لمبة التشغيل", (rows) => {
        rows[0]![0] = c("NO", "Q0.0");
        rows[0]![4] = c("COIL", "Q0.1");
      }, 1),
    ],
  );
}

function selfHolding(): Project {
  const p = startStop();
  p.name = "Self Holding";
  return p;
}

function forwardMotor(): Project {
  const p = startStop();
  p.name = "Forward Motor";
  p.tags = { "I0.0": "FWD_START", "I0.1": "STOP", "Q0.0": "KM_FWD" };
  return p;
}

function forwardReverse(): Project {
  const p = base(
    "Forward / Reverse",
    { "I0.0": "FWD", "I0.1": "STOP", "I0.2": "REV", "Q0.0": "KM_FWD", "Q0.1": "KM_REV" },
    [
      net("Network 1 — أمام", (rows, links) => {
        rows[0]![0] = c("NO", "I0.0");
        rows[0]![1] = c("NO", "I0.1");
        rows[0]![2] = c("NC", "Q0.1");
        rows[0]![4] = c("COIL", "Q0.0");
        rows[1]![0] = c("NO", "Q0.0");
        links[0]![1] = true;
      }),
      net("Network 2 — خلف", (rows, links) => {
        rows[0]![0] = c("NO", "I0.2");
        rows[0]![1] = c("NO", "I0.1");
        rows[0]![2] = c("NC", "Q0.0");
        rows[0]![4] = c("COIL", "Q0.1");
        rows[1]![0] = c("NO", "Q0.1");
        links[0]![1] = true;
      }),
    ],
  );
  const km2 = {
    id: "km2",
    type: "contactor",
    x: 1420,
    y: 300,
    label: "KM2",
    state: {},
    props: {},
  };
  const rev = {
    id: "pb3",
    type: "pb_start",
    x: 340,
    y: 260,
    label: "REVERSE",
    state: {},
    props: {},
  };
  p.devices.push(km2, rev);
  p.wires.push(
    w("psu1:+V", "pb3:13", "red"),
    w("pb3:14", "plc1:I0.2", "blue"),
    w("plc1:Q0.1", "km2:A1", "red"),
    w("km2:A2", "psu1:-V", "black"),
    w("s380:L1", "km2:1/L1", "black"),
    w("s380:L2", "km2:3/L2", "black"),
    w("s380:L3", "km2:5/L3", "black"),
  );
  return p;
}

function timerStart(): Project {
  return base(
    "Timer Start",
    { "I0.0": "START", "I0.1": "STOP", "Q0.0": "KM1", "Q0.1": "LAMP" },
    [
      net("Network 1 — تثبيت الطلب", (rows, links) => {
        rows[0]![0] = c("NO", "I0.0");
        rows[0]![1] = c("NO", "I0.1");
        rows[0]![4] = c("COIL", "M0.0");
        rows[1]![0] = c("NO", "M0.0");
        links[0]![1] = true;
      }),
      net("Network 2 — تأخير 5 ثوان", (rows) => {
        rows[0]![0] = c("NO", "M0.0");
        rows[0]![2] = c("TON", "T1", { preset: "5s" });
        rows[0]![5] = c("COIL", "Q0.0");
      }, 1),
    ],
  );
}

function trafficLights(): Project {
  const p = base(
    "Traffic Lights",
    { "I0.0": "START", "I0.1": "STOP", "Q0.0": "RED", "Q0.1": "AMBER", "Q0.2": "GREEN" },
    [
      net("Network 1 — تفعيل", (rows, links) => {
        rows[0]![0] = c("NO", "I0.0");
        rows[0]![1] = c("NO", "I0.1");
        rows[0]![4] = c("COIL", "M0.0");
        rows[1]![0] = c("NO", "M0.0");
        links[0]![1] = true;
      }),
      net("Network 2 — أحمر 5 ثوان", (rows) => {
        rows[0]![0] = c("NO", "M0.0");
        rows[0]![1] = c("TON", "T_RED", { preset: "5s" });
        rows[0]![5] = c("COIL", "M0.1");
      }, 1),
      net("Network 3 — أصفر 2 ثانية", (rows) => {
        rows[0]![0] = c("NO", "M0.1");
        rows[0]![1] = c("TON", "T_AMB", { preset: "2s" });
        rows[0]![5] = c("COIL", "M0.2");
      }, 1),
      net("Network 4 — المخارج", (rows) => {
        rows[0]![0] = c("NO", "M0.0");
        rows[0]![1] = c("NC", "M0.1");
        rows[0]![5] = c("COIL", "Q0.0");
        rows[1]![0] = c("NO", "M0.1");
        rows[1]![1] = c("NC", "M0.2");
        rows[1]![5] = c("COIL", "Q0.1");
      }),
      net("Network 5 — أخضر", (rows) => {
        rows[0]![0] = c("NO", "M0.2");
        rows[0]![5] = c("COIL", "Q0.2");
      }, 1),
    ],
  );
  const l2 = { id: "hl2", type: "lamp", x: 1060, y: 800, label: "AMBER", state: {}, props: { color: "amber" } };
  const l3 = { id: "hl3", type: "lamp", x: 1220, y: 800, label: "GREEN", state: {}, props: { color: "green" } };
  p.devices.push(l2, l3);
  p.wires.push(
    w("plc1:Q0.2", "hl2:X1", "red"),
    w("hl2:X2", "psu1:-V", "black"),
    w("plc1:Q0.2", "hl3:X1", "red"),
    w("hl3:X2", "psu1:-V", "black"),
  );
  return p;
}

function motorOverload(): Project {
  const p = base(
    "Motor with Overload",
    { "I0.0": "START", "I0.1": "STOP", "I0.2": "OVERLOAD", "Q0.0": "KM1" },
    [
      net("Network 1 — حماية حرارية", (rows, links) => {
        rows[0]![0] = c("NO", "I0.0");
        rows[0]![1] = c("NO", "I0.1");
        rows[0]![2] = c("NO", "I0.2");
        rows[0]![4] = c("COIL", "Q0.0");
        rows[1]![0] = c("NO", "Q0.0");
        links[0]![1] = true;
      }),
    ],
  );
  const ol = { id: "ol1", type: "overload", x: 1420, y: 560, label: "F1", state: {}, props: {} };
  p.devices.push(ol);
  p.wires.push(w("psu1:+V", "ol1:95", "red"), w("ol1:96", "plc1:I0.2", "blue"));
  return p;
}

function twoMotors(): Project {
  const p = forwardReverse();
  p.name = "Two Motors Sequence";
  p.tags = { "I0.0": "START", "I0.1": "STOP", "Q0.0": "M1", "Q0.1": "M2" };
  p.networks = [
    net("Network 1 — الماطور الأول", (rows, links) => {
      rows[0]![0] = c("NO", "I0.0");
      rows[0]![1] = c("NO", "I0.1");
      rows[0]![4] = c("COIL", "Q0.0");
      rows[1]![0] = c("NO", "Q0.0");
      links[0]![1] = true;
    }),
    net("Network 2 — الماطور الثاني بعد 3 ثوان", (rows) => {
      rows[0]![0] = c("NO", "Q0.0");
      rows[0]![1] = c("TON", "T2", { preset: "3s" });
      rows[0]![5] = c("COIL", "Q0.1");
    }, 1),
  ];
  return p;
}

export const EXAMPLES: { id: string; name: string; build: () => Project }[] = [
  { id: "startstop", name: "Start / Stop", build: startStop },
  { id: "selfhold", name: "Self Holding", build: selfHolding },
  { id: "fwd", name: "Forward Motor", build: forwardMotor },
  { id: "fwdrev", name: "Forward / Reverse", build: forwardReverse },
  { id: "timer", name: "Timer Start", build: timerStart },
  { id: "traffic", name: "Traffic Lights", build: trafficLights },
  { id: "overload", name: "Motor with Overload", build: motorOverload },
  { id: "two", name: "Two Motors Sequence", build: twoMotors },
];
